import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { ChartTooltip } from './ChartTooltip';
import {
  FAMILY_COLOR,
  FAMILY_LABEL,
  FAMILY_ORDER,
} from '../../../../../utils/chartColors';
import { buildDailyData, formatDayLabel } from '../../../../../features/dashboard/dashboardUtils';
import clsx from 'clsx';
import { CHART_MIN_HEIGHT } from '../../../../../features/dashboard/dashboardConstants';

/**
 * Shipments per day, stacked by state family.
 *
 * Bars and not an area on purpose: the dashboard opens on a three-day window,
 * and an area chart drawn through three points reads as a rendering glitch.
 * Bars are honest at three and still work at thirty, which is where this earns
 * its keep — widening the range costs the backend nothing now.
 *
 * Stacked by family rather than by status because nineteen stacked segments per
 * bar is not a chart, it is a texture.
 *
 * The body only: the card around it, its title and its tab live in
 * `DashboardCharts`, which shares them with the status donut.
 */
export const DailyVolumeChart = ({ daily = [], range, isLoading }) => {
  const data = useMemo(() => buildDailyData(daily, range), [daily, range]);
  const hasData = data.some((row) => row.total > 0);

  return (
    <div className="relative flex-1 min-h-0 flex flex-col">
      {/* Only the chart grows; the legend keeps its own height. It used to
          live inside this box and got clipped off the bottom. */}
      <div className={clsx('flex-1 min-h-0', CHART_MIN_HEIGHT)}>
        {isLoading && !hasData && (
          <div className="h-full rounded-xl bg-ninja-gray dark:bg-[#2d2d2d] animate-pulse" />
        )}

        {!isLoading && !hasData && (
          <div className="h-full flex items-center justify-center">
            <p className="text-sm text-text-muted">No hay envíos en el periodo seleccionado</p>
          </div>
        )}

        {hasData && (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="currentColor" className="text-border" />
              <XAxis
                dataKey="date"
                tickFormatter={formatDayLabel}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11 }}
                stroke="currentColor"
                className="text-text-muted"
              />
              <YAxis
                allowDecimals={false}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 11 }}
                stroke="currentColor"
                className="text-text-muted"
              />
              <Tooltip
                cursor={{ fill: 'currentColor', className: 'text-ninja-gray dark:text-[#2d2d2d]', opacity: 0.4 }}
                content={(props) => (
                  <ChartTooltip {...props} title={formatDayLabel(props.label)} showTotal />
                )}
              />
              {FAMILY_ORDER.map((family, index) => (
                <Bar
                  key={family}
                  dataKey={family}
                  name={FAMILY_LABEL[family]}
                  stackId="day"
                  fill={FAMILY_COLOR[family]}
                  // Only the topmost segment gets rounded corners, or every
                  // band in the stack looks like a separate bar.
                  radius={index === FAMILY_ORDER.length - 1 ? [4, 4, 0, 0] : 0}
                  maxBarSize={48}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {hasData && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 mt-4">
          {FAMILY_ORDER.map((family) => (
            <div key={family} className="flex items-center gap-1.5 text-xs text-text-secondary">
              <span
                className="w-2.5 h-2.5 rounded-full"
                style={{ backgroundColor: FAMILY_COLOR[family] }}
              />
              {FAMILY_LABEL[family]}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
